/**
 * ColorSwatchGrid — unified color picker following the Pipeline stage-picker style:
 * • grid of h-8/w-8 circles with subtle ring + Check icon (no black border)
 * • extra "+" swatch that opens a native colour picker for custom hex values.
 */
import { useRef } from "react";
import { Check, Palette } from "lucide-react";

export const DEFAULT_PALETTE = [
  // Harmonious Tailwind 400-level palette — coordinated saturation & luminance
  "#60A5FA", // blue-400
  "#22D3EE", // cyan-400
  "#2DD4BF", // teal-400
  "#4ADE80", // green-400
  "#FBBF24", // amber-400
  "#FB923C", // orange-400
  "#F87171", // red-400
  "#F472B6", // pink-400
  "#A78BFA", // violet-400
  "#94A3B8", // slate-400
];

export function ColorSwatchGrid({
  value,
  onChange,
  palette = DEFAULT_PALETTE,
  size = "md",         // "sm" | "md"
  columns = 6,
  testIdPrefix,
  className = "",
  allowCustom = false,
}) {
  const sz = size === "sm" ? "h-6 w-6" : "h-8 w-8";
  const checkSz = size === "sm" ? "h-3 w-3" : "h-4 w-4";
  const customRef = useRef(null);
  const isCustom = value && !palette.includes(value);
  return (
    <div
      className={`grid gap-3 ${className}`}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      data-testid={testIdPrefix ? `${testIdPrefix}-grid` : undefined}
    >
      {palette.map((c) => {
        const selected = value === c;
        return (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            style={{ background: c }}
            className={`relative ${sz} rounded-full ring-1 ring-[color:var(--sp-border)] hover:ring-2 hover:ring-[color:var(--sp-brand)]/60 hover:scale-110 transition-all ${selected ? "ring-2 ring-offset-2 ring-[color:var(--sp-brand)]" : ""}`}
            data-testid={testIdPrefix ? `${testIdPrefix}-${c}` : undefined}
            title={c}
            aria-label={c}
          >
            {selected && <Check className={`${checkSz} text-white absolute inset-0 m-auto`} strokeWidth={3} />}
          </button>
        );
      })}
      {allowCustom && (
        <button
          type="button"
          onClick={() => customRef.current?.click()}
          style={isCustom ? { background: value } : undefined}
          className={`relative ${sz} rounded-full ring-1 ring-[color:var(--sp-border)] hover:ring-2 hover:ring-[color:var(--sp-brand)]/60 hover:scale-110 transition-all inline-flex items-center justify-center ${isCustom ? "ring-2 ring-offset-2 ring-[color:var(--sp-brand)]" : "bg-white"}`}
          data-testid={testIdPrefix ? `${testIdPrefix}-custom` : undefined}
          title="Colore personalizzato"
          aria-label="Colore personalizzato"
        >
          {isCustom
            ? <Check className={`${checkSz} text-white`} strokeWidth={3} />
            : <Palette className={`${size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} text-[color:var(--sp-text-muted)]`} />}
          <input
            ref={customRef}
            type="color"
            value={isCustom ? value : "#000000"}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
            data-testid={testIdPrefix ? `${testIdPrefix}-custom-input` : undefined}
            aria-hidden
          />
        </button>
      )}
    </div>
  );
}
